import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Box, Typography, Button, Grid, TextField, CircularProgress, MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    getAllMakerPlanDetails, getAllMakerPlans
} from '../../../services/adminServices/MakerPlanServices';
import { getAllTopicsOfModule } from '../../../services/adminServices/ModuleServices';
import {
    addDailyPractice, getLevelCount, UpdateDailyPractice
} from '../../../services/mentorServices/DailyPracticeServices';

const INITIAL_FORM_STATE = {
    makerPlan: '',
    module: '',
    day: '',
    level1: 0,
    level2: 0,
    level3: 0,
    type: '',
    topic: '',
    codingSpeed: ''
};

const DailyPracticeModal = ({ open, onClose, selection, selectedRow, fetchData, setSelectedRow }) => {
    const [formValues, setFormValues] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [makerPlanOptions, setMakerPlanOptions] = useState([]);
    const [makerModuleOptions, setMakerModuleOptions] = useState([]);
    const [topicOptions, setTopicOptions] = useState([]);
    const [levelCount, setLevelCount] = useState([])

    useEffect(() => {
        if (!open) {
            resetFormState();
            setSelectedRow(null);
        }
    }, [open]);

    useEffect(() => {
        if (selectedRow) {
            fetchModules(selectedRow.MakerPlan?.makerplanID);
            fetchTopics(selectedRow.Maker_Module?.moduleID);
            setFormValues({
                makerPlan: selectedRow.MakerPlan?.makerplanID || '',
                module: selectedRow.Maker_Module?.moduleID || '',
                topic: selectedRow.Topic?.topicID || '',
                day: selectedRow.daily_practice_name,
                level1: selectedRow.level1_practice_num,
                level2: selectedRow.level2_practice_num,
                level3: selectedRow.level3_practice_num,
                type: selectedRow.coding_type,
                codingSpeed: selectedRow.coding_speed || ''
            });
        } else {
            resetFormState();
        }
        setFormErrors({});
    }, [selectedRow]);

    useEffect(() => {
        const fetchMakerPlans = async () => {
            try {
                const response = await getAllMakerPlans();
                setMakerPlanOptions(response?.data.data || []);
            } catch (error) {
                toast.error(error?.response?.data.message || error?.message || error?.error);
            }
        };
        fetchMakerPlans();
    }, []);

    const fetchModules = useCallback(async (makerPlanId) => {
        try {
            const response = await getAllMakerPlanDetails(makerPlanId);
            setMakerModuleOptions(response?.data.data || []);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const fetchTopics = useCallback(async (moduleID) => {
        try {
            const response = await getAllTopicsOfModule(moduleID);
            setTopicOptions(response?.data.data || []);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const resetFormState = () => {
        setFormValues(INITIAL_FORM_STATE);
        setFormErrors({});
        setIsLoading(false);
    };

    const handleInputChange = ({ target: { name, value } }) => {
        if (['makerPlan', 'module', 'type', 'topic', 'day', 'codingSpeed'].includes(name)) {
            if (name === 'makerPlan') fetchModules(value);
            if (name === 'module') fetchTopics(value);
            setFormValues((prev) => ({ ...prev, [name]: value }));
            return;
        }

        if (!/^[0-9]*$/.test(value)) {
            setFormErrors((prev) => ({ ...prev, [name]: 'Please enter valid numbers only' }));
        } else {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
            setFormValues((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validateFields = () => {
        const errors = {};
        const { makerPlan, module, day, level1, level2, level3, type, topic, codingSpeed } = formValues;

        if (!makerPlan) errors.makerPlan = 'Maker Plan is required*';
        if (!module) errors.module = 'Module is required*';
        if (!day ) errors.day = 'Day is required*';
        if (!parseInt(level1) && !parseInt(level2) && !parseInt(level3)) errors.level3 = 'At least one level field is required*';
        if (!type) errors.type = 'Type is required*';
        if (!topic) errors.topic = 'Topic Name is required*';
        if (!codingSpeed) errors.codingSpeed = 'Coding Speed is required*';

        return errors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateFields();
        if (Object.keys(errors).length) return setFormErrors(errors);

        setIsLoading(true);
        try {
            const payload = {
                coding_type: formValues.type,
                daily_practice_name: formValues.day,
                level1_practice_num: parseInt(formValues.level1, 10),
                level2_practice_num: parseInt(formValues.level2, 10),
                level3_practice_num: parseInt(formValues.level3, 10),
                coding_speed: formValues.codingSpeed
            };

            const response = selectedRow
                ? await UpdateDailyPractice(selectedRow.daily_practice_id, selection.coe, selection.lab, formValues.makerPlan, formValues.module, formValues.topic, payload)
                : await addDailyPractice(selection.coe, selection.lab, formValues.makerPlan, formValues.module, formValues.topic, payload);

            toast.success(response.data.message);
            resetFormState();
            onClose();
            fetchData();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (formValues.module && formValues.topic) {
            getLevelCount(formValues.module, formValues.topic)
                .then((response) => setLevelCount(response?.data?.data.practiceCounts || []))
                .catch(() => setLevelCount([]));
        }
    }, [formValues.module, formValues.topic]);
    const codingSpeedOptions = ['Fast', 'Regular', 'Slow']
    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '45%',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: "1.3rem 2rem ",
                    borderRadius: 2,
                    maxHeight: '92vh',       
                    overflowY: 'auto'  
                }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>{selectedRow ? "Update Daily Practice" : "Create Daily Practice"}</Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {[
                            { label: 'Type *', name: 'type', type: 'select', options: ['Practice', 'Test'], error: formErrors.type },
                            { label: 'Maker Plan *', name: 'makerPlan', type: 'select', options: makerPlanOptions, error: formErrors.makerPlan },
                            { label: 'Module *', name: 'module', type: 'select', options: makerModuleOptions, error: formErrors.module },
                            { label: 'Topic Name *', name: 'topic', type: 'select', options: topicOptions, error: formErrors.topic },
                            { label: 'Coding Speed *', name: 'codingSpeed', type: 'select', options: codingSpeedOptions, error: formErrors.codingSpeed },
                            { label: 'Day *', name: 'day', type: 'text', error: formErrors.day },
                            { label: `Level 1  (${ levelCount.length>0?levelCount[0]?.count:0})`, name: 'level1', type: 'text', error: formErrors.level1, inputProps: { min: 0, max: 100, pattern: '[0-9]*' } },
                            { label: `Level 2  (${ levelCount.length>0?levelCount[1]?.count:0})`,name: 'level2', type: 'text', error: formErrors.level2, inputProps: { min: 0, max: 100, pattern: '[0-9]*' } },
                            { label: `Level 3  (${ levelCount.length>0?levelCount[2]?.count:0})`,name: 'level3', type: 'text', error: formErrors.level3, inputProps: { min: 0, max: 100, pattern: '[0-9]*' } },
                        ].map(({ label, name, type, options, error, ...props }) => (
                            <Grid item xs={12} key={name}>
                                {type === 'select' ? (
                                    <TextField
                                        fullWidth
                                        select
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        size='small'
                                    >
                                        {name === "type" && options.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                        {name === "makerPlan" && options.map((option, index) => (
                                            <MenuItem key={index} value={option.makerplanID}>{option.makerplan_Name}</MenuItem>
                                        ))}
                                        {name === "module" && options.Maker_Modules?.map((option, index) => (
                                            <MenuItem key={index} value={option.moduleID}>{option.module_Name}</MenuItem>
                                        ))}
                                        {name === "topic" && options?.map((option, index) => (
                                            <MenuItem key={index} value={option.topicID}>{option.topic_Name}</MenuItem>
                                        ))}
                                        {name === "codingSpeed" && options?.map((option, index) => (
                                            <MenuItem key={index} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth
                                        size='small'
                                        type={type}
                                        label={label}
                                        name={name}
                                        value={formValues[name]}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        helperText={error}
                                        {...props}
                                    />
                                )}
                            </Grid>
                        ))}
                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                sx={{ marginRight: 2 }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Submit'}
                            </Button>
                            <Button variant="outlined" color="secondary" onClick={onClose}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
};

export default DailyPracticeModal;
